// Closed-form means, PDF curves, and label formatting for informative priors.
// Ported from the Streamlit prototype (_compute_prior_mean / _compute_prior_pdf /
// _format_prior_params / _fmt_mean) so the About-tab charts match exactly.

import { MediaPrior } from './data-qa-types';

export interface PdfPoint {
  x: number;
  y: number;
}

// Distribution names the Meridian prior schema (and this module) understand. A
// prior must name one of these with a finite primary parameter to be renderable.
const MERIDIAN_DISTRIBUTIONS = new Set([
  'lognormal',
  'normal',
  'halfnormal',
  'half_normal',
  'half-normal',
  'beta',
  'uniform'
]);

/**
 * True when a prior conforms to the Meridian schema this card renders. Legacy
 * pre-Meridian priors (e.g. zerowater's) don't — per the econ team they should
 * be ignored, so the About tab drops them and shows a "not available" message
 * rather than attempting to chart them.
 */
export const isMeridianPrior = (prior: MediaPrior): boolean =>
  MERIDIAN_DISTRIBUTIONS.has((prior.distribution || '').trim().toLowerCase()) &&
  Number.isFinite(prior.param1);

const PARAM_LABELS: Record<string, string[]> = {
  lognormal: ['μ', 'σ'],
  normal: ['μ', 'σ'],
  halfnormal: ['σ'],
  beta: ['α', 'β'],
  uniform: ['low', 'high']
};

const linspace = (lo: number, hi: number, n: number): number[] => {
  if (n <= 1) return [lo];
  const step = (hi - lo) / (n - 1);
  return Array.from({ length: n }, (_, i) => lo + i * step);
};

// Lanczos approximation of ln Γ(x) — needed only for the Beta PDF.
const lgamma = (x: number): number => {
  const g = 7;
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
    -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
    1.5056327351493116e-7
  ];
  if (x < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * x)) - lgamma(1 - x);
  }
  const xa = x - 1;
  let a = c[0];
  const t = xa + g + 0.5;
  for (let i = 1; i < g + 2; i += 1) {
    a += c[i] / (xa + i);
  }
  return 0.5 * Math.log(2 * Math.PI) + (xa + 0.5) * Math.log(t) - t + Math.log(a);
};

/** Closed-form mean of the named distribution, or null if unsupported/invalid. */
export const computePriorMean = (distribution: string, p1: number, p2?: number): number | null => {
  const dist = (distribution || '').trim().toLowerCase();
  const a = p1;
  const b = p2;
  if (a === undefined || a === null || Number.isNaN(a)) return null;
  switch (dist) {
    case 'lognormal':
      if (b === undefined || b <= 0) return null;
      return Math.exp(a + (b * b) / 2);
    case 'normal':
      return a;
    case 'halfnormal':
    case 'half_normal':
    case 'half-normal':
      if (a <= 0) return null;
      return a * Math.sqrt(2 / Math.PI);
    case 'beta':
      if (b === undefined || a <= 0 || b <= 0) return null;
      return a / (a + b);
    case 'uniform':
      if (b === undefined || b <= a) return null;
      return (a + b) / 2;
    default:
      return null;
  }
};

/** Sampled PDF curve for the named distribution, or null if unsupported/invalid. */
export const computePriorPdf = (
  distribution: string,
  p1: number,
  p2?: number
): PdfPoint[] | null => {
  const dist = (distribution || '').trim().toLowerCase();
  const a = p1;
  const b = p2;
  if (a === undefined || a === null || Number.isNaN(a)) return null;
  const toPoints = (xs: number[], ys: number[]): PdfPoint[] => xs.map((x, i) => ({ x, y: ys[i] }));

  if (dist === 'lognormal') {
    const mu = a;
    const sigma = b;
    if (sigma === undefined || sigma <= 0) return null;
    const xLo = Math.max(1e-6, Math.exp(mu - 4 * sigma) * 0.1);
    const xHi = Math.exp(mu + 4 * sigma);
    const xs = linspace(xLo, xHi, 400);
    const ys = xs.map(
      x =>
        (1 / (x * sigma * Math.sqrt(2 * Math.PI))) *
        Math.exp(-((Math.log(x) - mu) ** 2) / (2 * sigma * sigma))
    );
    return toPoints(xs, ys);
  }
  if (dist === 'normal') {
    const mu = a;
    const sigma = b;
    if (sigma === undefined || sigma <= 0) return null;
    const xs = linspace(mu - 4 * sigma, mu + 4 * sigma, 300);
    const ys = xs.map(
      x => (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-((x - mu) ** 2) / (2 * sigma * sigma))
    );
    return toPoints(xs, ys);
  }
  if (dist === 'halfnormal' || dist === 'half_normal' || dist === 'half-normal') {
    const sigma = a;
    if (sigma <= 0) return null;
    const xs = linspace(0, 4 * sigma, 300);
    const ys = xs.map(
      x => (Math.sqrt(2) / (sigma * Math.sqrt(Math.PI))) * Math.exp(-(x * x) / (2 * sigma * sigma))
    );
    return toPoints(xs, ys);
  }
  if (dist === 'beta') {
    const alpha = a;
    const betaP = b;
    if (betaP === undefined || alpha <= 0 || betaP <= 0) return null;
    const logNorm = lgamma(alpha + betaP) - lgamma(alpha) - lgamma(betaP);
    const xs = linspace(1e-4, 1 - 1e-4, 300);
    const ys = xs.map(x =>
      Math.exp(logNorm + (alpha - 1) * Math.log(x) + (betaP - 1) * Math.log(1 - x))
    );
    return toPoints(xs, ys);
  }
  if (dist === 'uniform') {
    const low = a;
    const high = b;
    if (high === undefined || high <= low) return null;
    const rng = high - low;
    const xs = linspace(low - 0.05 * rng, high + 0.05 * rng, 200);
    const ys = xs.map(x => (x >= low && x <= high ? 1 / rng : 0));
    return toPoints(xs, ys);
  }
  return null;
};

// 4 significant figures, trailing zeros trimmed — matches Python's `%.4g`.
const sig4 = (v: number): string => Number(v.toPrecision(4)).toString();

/** Compact textual summary, e.g. `LogNormal(μ=-3.96, σ=0.9)`. */
export const formatPriorParams = (distribution: string, p1: number, p2?: number): string => {
  const dist = (distribution || '').trim();
  const labels = PARAM_LABELS[dist.toLowerCase()] ?? [];
  const parts: string[] = [];
  [p1, p2].forEach((raw, i) => {
    if (raw === undefined || raw === null || Number.isNaN(raw)) return;
    const label = labels[i] ?? `p${i + 1}`;
    parts.push(`${label}=${sig4(raw)}`);
  });
  return parts.length ? `${dist}(${parts.join(', ')})` : dist || 'unknown';
};

/**
 * Adaptive-precision mean: .3f for normal-range values, more decimals for tiny
 * ones, trailing zeros trimmed for values ≥ 1, scientific only as last resort.
 */
export const formatPriorMean = (x: number | null): string => {
  if (x === null || x === undefined) return '';
  if (x === 0) return '0';
  const absX = Math.abs(x);
  if (absX >= 1) {
    const s = x.toFixed(3);
    return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
  }
  if (absX >= 0.001) return x.toFixed(3);
  const decimals = 3 - Math.floor(Math.log10(absX)) - 1;
  if (decimals > 9) return x.toExponential(2);
  return x.toFixed(decimals);
};
